// The account's Orpheus billing settings (v3 `orpheusSettingsStore.ts`): open providers, both band
// sets, the stored key and this cycle's own-key usage. Shared by the consent screen and the
// settings pane, so a key added in one shows up in the other.
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { OrpheusSettingsApi } from '../api/OrpheusSettingsApi'
import type {
  OrpheusApiKeySummaryDto,
  OrpheusKeyCheckResultDto,
  OrpheusProvider,
  OrpheusSettingsDto,
} from '../models/orpheusKey.types'

export const useOrpheusSettingsStore = defineStore('orpheusSettingsStore', () => {
  const api = new OrpheusSettingsApi()

  const settings = shallowRef<OrpheusSettingsDto | null>(null)
  /** The account `settings` belongs to — another account refetches. */
  const accountId = ref<number | string | null>(null)
  const loading = ref(false)
  const error = ref(false)

  const apiKey = computed(() => settings.value?.api_key ?? null)
  const providers = computed<OrpheusProvider[]>(() => settings.value?.providers ?? [])

  async function fetch(account: number | string | undefined, force = false): Promise<void> {
    if (account === undefined) {
      reset()
      return
    }
    if (String(accountId.value) !== String(account)) {
      settings.value = null
      error.value = false
    } else if (loading.value || (settings.value && !force)) {
      return
    }

    accountId.value = account
    loading.value = true
    try {
      const next = await api.fetch(account)
      // The account may have changed while this was in flight.
      if (String(accountId.value) === String(account)) {
        settings.value = next
        error.value = false
      }
    } catch {
      if (String(accountId.value) === String(account)) error.value = true
    } finally {
      if (String(accountId.value) === String(account)) loading.value = false
    }
  }

  function setApiKey(next: OrpheusApiKeySummaryDto | null): void {
    if (settings.value) settings.value = { ...settings.value, api_key: next }
  }

  /** Rejects with the provider's typed error when the key doesn't pass; nothing is stored then. */
  async function saveKey(
    account: number | string,
    provider: OrpheusProvider,
    rawKey: string,
  ): Promise<OrpheusApiKeySummaryDto | null> {
    const saved = await api.saveKey(account, provider, rawKey.trim())
    setApiKey(saved)
    return saved
  }

  async function removeKey(account: number | string): Promise<void> {
    await api.removeKey(account)
    setApiKey(null)
  }

  async function checkKey(account: number | string): Promise<OrpheusKeyCheckResultDto> {
    const result = await api.checkKey(account)
    setApiKey(result?.api_key ?? null)
    return result
  }

  async function setFallback(account: number | string, enabled: boolean): Promise<void> {
    const value = await api.setFallback(account, enabled)
    if (settings.value) settings.value = { ...settings.value, fallback_to_credits: value }
  }

  function reset(): void {
    settings.value = null
    accountId.value = null
    loading.value = false
    error.value = false
  }

  return {
    settings,
    accountId,
    loading,
    error,
    apiKey,
    providers,
    fetch,
    saveKey,
    removeKey,
    checkKey,
    setFallback,
    reset,
  }
})
